import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import type { TelegramAuthData } from '@/types';

/**
 * Hook для работы с аутентификацией
 * @contract Предоставляет методы для login/logout и проверки auth
 */
export function useAuth() {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    loginWithTelegram,
    logout,
    checkAuth: storeCheckAuth,
    clearError,
  } = useAuthStore();

  const navigate = useNavigate();

  // Проверка аутентификации при монтировании
  // Используем useCallback для стабилизации функции
  const checkAuthOnce = useCallback(() => {
    storeCheckAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Пустой массив - выполняется только один раз

  useEffect(() => {
    checkAuthOnce();
  }, [checkAuthOnce]);

  // Обёртка для login с навигацией
  const handleLogin = async (data: TelegramAuthData) => {
    await loginWithTelegram(data);
    navigate('/dashboard');
  };

  // Обёртка для logout с навигацией
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login: handleLogin,
    logout: handleLogout,
    clearError,
  };
}

/**
 * Hook для защиты роутов
 * Редиректит на login если пользователь не аутентифицирован
 */
export function useRequireAuth() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  return { isAuthenticated, isLoading };
}
